import { useState, useCallback } from "react";
import { useQuery, useAction } from "convex/react";
import { api } from "../../../convex/_generated/api";
import type { Id } from "../../../convex/_generated/dataModel";
import { type QueryState, wrapQuery } from "../queryState";
import type { AssetPipelineVM } from "../types/execution";
import { useSubscriptionActive } from "../components/SubscriptionScope";

/**
 * useAssetPipeline — wraps assetPipeline composite (PRD Section 8)
 */
export function useAssetPipeline(
  projectId: Id<"projects">
): QueryState<AssetPipelineVM> {
  const active = useSubscriptionActive();
  const raw = useQuery(
    api.composites.assetPipeline.assetPipeline,
    active ? { projectId } : "skip"
  );
  return wrapQuery(raw, (r) => r as AssetPipelineVM);
}

/**
 * useAssetList — wraps assets.list / assets.listInbox queries.
 *
 * Provides the full asset documents needed by AssetViewer (cdnUrl, taskId,
 * executionOrder, etc.) that the AssetPipeline composite does not include.
 * Supports both project-scoped and inbox-scoped (repositoryId) listing.
 */
export function useAssetList(opts: {
  projectId?: Id<"projects">;
  repositoryId?: Id<"repositories">;
  workflowStatus?: string;
}): QueryState<any[]> {
  const active = useSubscriptionActive();
  const { projectId, repositoryId, workflowStatus } = opts;

  const projectAssets = useQuery(
    api.assets.list,
    active && projectId ? { projectId, workflowStatus } : "skip"
  );
  const inboxAssets = useQuery(
    api.assets.listInbox,
    active && repositoryId && !projectId ? { repositoryId, workflowStatus } : "skip"
  );

  const raw = projectId ? projectAssets : inboxAssets;
  return wrapQuery(raw, (r) => r);
}

/**
 * useFileUploader — multi-file upload hook that wraps Convex actions.
 *
 * Encapsulates requestUploadUrls + PUT + confirmUploads into a single hook
 * so that components have zero direct useAction calls.
 * States: idle -> uploading -> confirming -> done -> (auto-reset to idle)
 * On error: -> error (reset() returns to idle)
 */
type FileUploaderState = "idle" | "uploading" | "confirming" | "done" | "error";

export function useFileUploader(opts: {
  repositoryId?: Id<"repositories">;
  projectId?: Id<"projects">;
  type?: "input" | "output";
}) {
  const requestUpload = useAction(api.assets.requestUploadUrls);
  const confirmUpload = useAction(api.assets.confirmUploads);
  const [state, setState] = useState<FileUploaderState>("idle");
  const [progress, setProgress] = useState("");
  const [error, setError] = useState("");

  const upload = useCallback(
    async (files: File[]) => {
      const { repositoryId, projectId, type: assetType = "input" } = opts;
      if (!repositoryId && !projectId) return;
      if (files.length === 0) return;

      setState("uploading");
      setError("");
      setProgress(`Requesting upload URLs for ${files.length} file(s)...`);

      try {
        const urlResults = await requestUpload({
          repositoryId,
          projectId,
          type: assetType,
          files: files.map((f) => ({
            fileName: f.name,
            contentType: f.type || "application/octet-stream",
          })),
        });

        const assetIds: Id<"assets">[] = [];
        for (let i = 0; i < urlResults.length; i++) {
          const { assetId, uploadUrl, fileName } = urlResults[i];
          const file = files[i];
          setProgress(`Uploading ${fileName} (${i + 1}/${files.length})...`);

          const res = await fetch(uploadUrl, {
            method: "PUT",
            headers: { "Content-Type": file.type || "application/octet-stream" },
            body: file,
          });
          if (!res.ok) throw new Error(`Failed to upload ${fileName}: ${res.status}`);
          assetIds.push(assetId);
        }

        setState("confirming");
        setProgress("Confirming uploads...");
        const confirmResults = await confirmUpload({ assetIds });
        const failed = confirmResults.filter((r) => r.status === "failed");
        if (failed.length > 0) {
          throw new Error(
            `${failed.length} file(s) failed confirmation: ${failed.map((f) => f.fileName).join(", ")}`
          );
        }

        setState("done");
        setProgress(`${files.length} file(s) uploaded successfully`);
        setTimeout(() => {
          setState("idle");
          setProgress("");
        }, 2000);
      } catch (err: unknown) {
        setState("error");
        setError(err instanceof Error ? err.message : String(err));
      }
    },
    [opts.repositoryId, opts.projectId, opts.type, requestUpload, confirmUpload]
  );

  const reset = useCallback(() => {
    setState("idle");
    setError("");
    setProgress("");
  }, []);

  const busy = state === "uploading" || state === "confirming";

  return { state, progress, error, busy, upload, reset };
}

// useAssetUpload is exported from its own file for cleaner test isolation
export { useAssetUpload } from "./useAssetUpload";
